/**
 * Attribution and legal links.
 *
 * Used on the login page and throughout the signed-in app, so the links are
 * reachable from anywhere rather than only before someone signs in.
 *
 * Both URLs are configuration and each renders only when set. A link to a
 * page that does not exist is worse than no link, particularly a legal one.
 */

import { useEffect, useState } from 'react';

interface AppConfig {
  privacyPolicyUrl: string | null;
  termsOfServiceUrl: string | null;
}

/* Fetched once per page load and shared. Every screen renders this footer,
 * and it would otherwise issue an identical request on each navigation. */
let cached: Promise<AppConfig> | null = null;

function loadConfig(): Promise<AppConfig> {
  cached ??= fetch('/api/config')
    .then((r) => (r.ok ? (r.json() as Promise<AppConfig>) : { privacyPolicyUrl: null, termsOfServiceUrl: null }))
    .catch(() => ({ privacyPolicyUrl: null, termsOfServiceUrl: null }));
  return cached;
}

export function useAppConfig(): AppConfig | null {
  const [config, setConfig] = useState<AppConfig | null>(null);
  useEffect(() => {
    let live = true;
    void loadConfig().then((c) => {
      if (live) setConfig(c);
    });
    return () => {
      live = false;
    };
  }, []);
  return config;
}

/**
 * A drawn heart rather than the emoji character.
 *
 * FPS standards are "Lucide React only, never emoji in the UI". This reads
 * identically, scales with the type, and takes its colour from CSS instead of
 * whatever the viewer's platform decides an emoji should look like.
 */
function Heart() {
  return (
    <svg className="legal-heart" viewBox="0 0 24 24" aria-label="love" role="img">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

export default function LegalFooter({ variant = 'page' }: { variant?: 'page' | 'app' }) {
  const config = useAppConfig();
  const hasLinks = Boolean(config?.privacyPolicyUrl || config?.termsOfServiceUrl);

  /* Attribution belongs on the way in, not on every screen afterwards. Once
   * someone is signed in they know whose software this is, and repeating it
   * under every page is noise. The legal links stay, since those need to be
   * reachable from anywhere. */
  const showAttribution = variant === 'page';

  // Nothing to render at all inside the app until a legal URL is configured.
  if (!showAttribution && !hasLinks) return null;

  return (
    <div className={`legal-footer legal-footer--${variant}`}>
      {showAttribution && (
        <div className="legal-made">
          Made with <Heart /> by the team at Front Porch Solutions
        </div>
      )}
      {hasLinks && (
        <div className="legal-links">
          {config?.privacyPolicyUrl && (
            <a href={config.privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          )}
          {config?.privacyPolicyUrl && config?.termsOfServiceUrl && (
            <span className="legal-sep">·</span>
          )}
          {config?.termsOfServiceUrl && (
            <a href={config.termsOfServiceUrl} target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>
          )}
        </div>
      )}
    </div>
  );
}
